import { Link } from "react-router-dom";
import React from "react";
import {
  FiArrowUpRight,
  FiBookOpen,
  FiCpu,
  FiGlobe,
  FiLayers,
  FiMessageCircle,
  FiSmartphone,
  FiTrendingUp,
  FiUsers,
  FiCheck,
} from "react-icons/fi";

import educationHero from "../assets/education-hero.png";
import "../styles/Education.css";

const capabilities = [
  {
    icon: <FiUsers />,
    title: "Connected communities",
    text: "Bring learners, families, educators, and administrators together through useful, timely digital touchpoints.",
  },
  {
    icon: <FiGlobe />,
    title: "High-performing web platforms",
    text: "Create accessible education websites that help prospective learners discover programs and take action.",
  },
  {
    icon: <FiSmartphone />,
    title: "Learning on every device",
    text: "Give students intuitive mobile access to resources, updates, learning journeys, and campus services.",
  },
  {
    icon: <FiCpu />,
    title: "Intelligent learning systems",
    text: "Use automation and meaningful insight to make learning experiences more responsive and personal.",
  },
];

const services = [
  {
    title: "Education marketing",
    text: "Reach the right learners with a clear message, thoughtful campaigns, and measurable growth.",
    tag: "Growth",
  },
  {
    title: "Search visibility",
    text: "Help courses, programs, and services appear when families and students are actively looking.",
    tag: "Discovery",
  },
  {
    title: "Admissions journeys",
    text: "Turn interest into enrolment with streamlined inquiry, application, and communication flows.",
    tag: "Experience",
  },
  {
    title: "Learning platforms",
    text: "Design connected learning environments that support courses, content, and everyday academic work.",
    tag: "Platform",
  },
  {
    title: "Mobile products",
    text: "Keep essential learning and campus experiences close at hand with purposeful mobile apps.",
    tag: "Mobile",
  },
  {
    title: "Operational automation",
    text: "Reduce repetitive work and give teams more time for the human side of education.",
    tag: "Efficiency",
  },
];

const journey = [
  {
    number: "01",
    title: "Listen",
    text: "We map your learners, teams, goals, and current systems to find the opportunities that matter.",
  },
  {
    number: "02",
    title: "Design",
    text: "We shape a focused digital plan and an experience that feels clear, capable, and distinctly yours.",
  },
  {
    number: "03",
    title: "Build",
    text: "We develop dependable technology with the flexibility to evolve alongside your institution.",
  },
  {
    number: "04",
    title: "Grow",
    text: "We measure, refine, and improve so your digital investment keeps creating value.",
  },
];

const proofPoints = [
  {
    icon: <FiBookOpen />,
    text: "Flexible learning ecosystems",
  },
  {
    icon: <FiLayers />,
    text: "Scalable, integrated platforms",
  },
  {
    icon: <FiMessageCircle />,
    text: "Stronger everyday communication",
  },
  {
    icon: <FiTrendingUp />,
    text: "Decisions guided by insight",
  },
];

const onlineImages = {
  learning:
    "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1600&q=85",

  technology:
    "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1600&q=85",

  classroom:
    "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1600&q=85",

  campus:
    "https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=1600&q=85",
};

function Education() {
  return (
    <main className="education-page">
      {/* HERO */}
      <section
        className="education-hero"
        style={{ backgroundImage: `url(${educationHero})` }}
      >
        <div className="education-grid-overlay" />
        <div className="education-glow education-glow-one" />
        <div className="education-glow education-glow-two" />

        <div className="education-hero-content reveal-up">
          <div className="education-kicker-wrap">
            <span className="education-kicker">
              Education technology
            </span>
            <span className="kicker-line" />
          </div>

          <h1>
            Learning built for{" "}
            <em>what&apos;s next</em>
          </h1>

          <p>
            Techlynx helps education organizations create clearer learning
            journeys, stronger communities, and technology that makes every
            day easier.
          </p>

          <div className="hero-actions">
            <a
              href="#education-solutions"
              className="education-button"
            >
              Explore solutions
              <FiArrowUpRight />
            </a>

            <div className="hero-stat">
              <strong>01</strong>
              <span>Education<br />ecosystem</span>
            </div>
          </div>
        </div>

        <div className="hero-visual">
          <div className="hero-image-frame">
            <img
              src={onlineImages.learning}
              alt="Students collaborating with technology"
            />

            <div className="hero-image-overlay" />

            <div className="hero-floating-card">
              <span className="floating-dot" />
              <div>
                <strong>CONNECTED</strong>
                <small>Learning experiences</small>
              </div>
            </div>
          </div>

          <div className="hero-orbit orbit-one" />
          <div className="hero-orbit orbit-two" />
        </div>

        <div className="hero-scroll">
          <span>SCROLL TO EXPLORE</span>
          <i />
        </div>
      </section>

      {/* INTRO */}
      <section
        className="education-intro"
        id="education-solutions"
      >
        <div className="section-topline">
          <span className="section-label">
            What we make possible
          </span>

          <span className="section-index">
            01 / 05
          </span>
        </div>

        <div className="intro-layout">
          <div className="intro-heading reveal-up">
            <h2>
              Digital experiences that keep{" "}
              <em>people</em> at the center
            </h2>
          </div>

          <div className="intro-copy reveal-up">
            <p>
              From the first program search to a confident graduation,
              we connect the moments that shape a modern education
              experience.
            </p>

            <div className="intro-line" />
          </div>
        </div>

        <div className="intro-feature">
          <div className="intro-feature-image">
            <img
              src={onlineImages.technology}
              alt="Students working together with technology"
              loading="lazy"
            />

            <div className="image-shade" />

            <div className="image-label">
              <span>TECHLYNX / EDUCATION</span>
              <strong>Learning connected.</strong>
            </div>
          </div>

          <div className="intro-feature-content">
            <span>THE HUMAN LAYER</span>

            <h3>
              Technology should make education feel more{" "}
              <em>human</em>
            </h3>

            <p>
              We bring together strategy, experience design, software,
              automation, and intelligent systems to help education
              organizations create meaningful digital journeys.
            </p>

            <div className="feature-list">
              <div>
                <FiCheck />
                <span>Designed around real learner needs</span>
              </div>

              <div>
                <FiCheck />
                <span>Built for connected education teams</span>
              </div>

              <div>
                <FiCheck />
                <span>Ready to evolve with your institution</span>
              </div>
            </div>
          </div>
        </div>

        <div className="capability-grid">
          {capabilities.map((item, index) => (
            <article
              className="capability-card reveal-up"
              style={{ "--delay": `${index * 90}ms` }}
              key={item.title}
            >
              <div className="capability-number">
                0{index + 1}
              </div>

              <div className="capability-icon">
                {item.icon}
              </div>

              <h3>{item.title}</h3>

              <p>{item.text}</p>

              <span className="card-corner" />
            </article>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="education-services">
        <div className="services-background-grid" />

        <div className="services-heading">
          <div>
            <div className="section-topline">
              <span className="section-label">
                A connected toolkit
              </span>

              <span className="section-index">
                02 / 05
              </span>
            </div>

            <h2>
              Technology with a{" "}
              <em>teaching purpose</em>
            </h2>
          </div>

          <p>
            From discovery to delivery, we create digital systems
            that support the complete education ecosystem.
          </p>
        </div>

        <div className="service-feature">
          <div className="service-feature-content">
            <span className="service-feature-label">
              DIGITAL EDUCATION / 2026
            </span>

            <h3>
              Build an education platform prepared for the next
              generation
            </h3>

            <p>
              Modern learners expect simple, fast, connected
              experiences across every touchpoint.
            </p>

            <Link to="/contact" className="text-link">
              Start a conversation
              <FiArrowUpRight />
            </Link>
          </div>

          <div className="service-feature-image">
            <img
              src={onlineImages.classroom}
              alt="Modern digital classroom"
              loading="lazy"
            />
            <div className="image-shade" />
          </div>
        </div>

        <div className="service-grid">
          {services.map((service, index) => (
            <article
              className="service-card reveal-up"
              style={{ "--delay": `${index * 70}ms` }}
              key={service.title}
            >
              <div className="service-card-top">
                <span>{service.tag}</span>
                <strong>0{index + 1}</strong>
              </div>

              <div className="service-arrow">
                <FiArrowUpRight />
              </div>

              <h3>{service.title}</h3>

              <p>{service.text}</p>

              <div className="service-progress">
                <span />
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* JOURNEY */}
      <section className="education-journey">
        <div className="journey-glow" />

        <div className="section-topline">
          <span className="section-label">
            How we work
          </span>

          <span className="section-index">
            03 / 05
          </span>
        </div>

        <div className="journey-heading">
          <h2>
            From insight to{" "}
            <em>impact</em>
          </h2>

          <p>
            A practical delivery path that turns big education
            ambitions into useful, durable digital products.
          </p>
        </div>

        <div className="journey-track">
          {journey.map((step, index) => (
            <article
              className="journey-step reveal-up"
              style={{ "--delay": `${index * 110}ms` }}
              key={step.number}
            >
              <div className="journey-step-number">
                <span>{step.number}</span>
              </div>

              <div className="journey-step-line" />

              <h3>{step.title}</h3>

              <p>{step.text}</p>

              <span className="journey-arrow">
                <FiArrowUpRight />
              </span>
            </article>
          ))}
        </div>
      </section>

      {/* PROOF */}
      <section className="education-proof">
        <div className="proof-image">
          <img
            src={onlineImages.campus}
            alt="Modern university campus"
            loading="lazy"
          />

          <div className="proof-image-overlay" />

          <div className="proof-image-caption">
            <span>01 — EDUCATION</span>
            <strong>Designed for progress.</strong>
          </div>
        </div>

        <div className="proof-content">
          <div className="section-topline">
            <span className="section-label">
              Built to move forward
            </span>

            <span className="section-index">
              04 / 05
            </span>
          </div>

          <h2>
            Confident technology for every stage of{" "}
            <em>learning</em>
          </h2>

          <p className="proof-description">
            We combine thoughtful experiences with dependable
            technology so institutions can focus on teaching,
            learning, and creating better outcomes.
          </p>

          <div className="proof-points">
            {proofPoints.map((point) => (
              <div className="proof-point" key={point.text}>
                <span className="proof-icon">
                  {point.icon}
                </span>

                <span>{point.text}</span>

                <FiArrowUpRight className="proof-arrow" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="education-cta">
        <div className="cta-grid" />
        <div className="cta-orb cta-orb-one" />
        <div className="cta-orb cta-orb-two" />

        <div className="cta-content reveal-up">
          <span className="section-label">
            Start a conversation
          </span>

          <div className="cta-number">
            05 / 05
          </div>

          <h2>
            Let&apos;s build an education experience people{" "}
            <em>remember</em>
          </h2>

          <p>
            Have an education platform, digital transformation,
            automation, or product idea? Let&apos;s turn it into
            something useful.
          </p>

          <Link
            to="/contact"
            className="education-button cta-button"
          >
            Talk to Techlynx
            <FiArrowUpRight />
          </Link>
        </div>
      </section>
    </main>
  );
}

export default Education;